import { Placement } from "@popperjs/core";
import classNames from "classnames";
import React, {
    ForwardedRef, MutableRefObject, ReactNode,
    useEffect,
    useState,
} from "react";
import { Popover as BootstrapPopover, OverlayTrigger } from "react-bootstrap";
import { OverlayInjectedProps } from "react-bootstrap/esm/Overlay";
import { PopperRef } from "react-bootstrap/esm/types";
import { FiHelpCircle } from "react-icons/fi";
import { RiInformationLine } from "@remixicon/react";
import Styles from "./Popover.module.scss";
import { useTheme } from "../../contexts/Theme";
import Icon, { IconSize as IconSizeVariant, IconVariant } from "../theme/icon/Icon";
import Button from "../theme/button/Button";

type IconSize = "xs" | "sm" | "md" | "lg";
export interface Props {
    buttonClassName?: string;
    title?: ReactNode;
    children: ReactNode;
    size?: "sm" | "md" | "lg";
    iconSize?: IconSize;
    variant?: "primary" | "tooltip";
    placement?: Placement;
    text?: ReactNode;
    popoverClassName?: string;
    hasArrow?: boolean;
    iconVariant?: IconVariant;
}
export interface PopoverController {
    hide: () => void;
    scheduleUpdate: () => void;
}

const ICON_SIZE_MAP: Record<IconSize, IconSizeVariant> = {
    xs: "xsmall",
    sm: "small",
    md: "medium",
    lg: "large",
};

interface PopperProps extends Omit<OverlayInjectedProps, "ref"> {
    title?: ReactNode;
    size?: "sm" | "md" | "lg";
    variant?: "primary" | "tooltip";
    popoverClassName?: string;
    hasArrow?: boolean;
}

const UpdatingPopper = React.forwardRef((args: PopperProps, ref: ForwardedRef<HTMLDivElement>) => {
    const {
        popper, children, show, popoverClassName, size,
        title, popperRef, variant,
        ...props
    } = args;

    useEffect(() => {
        if (popperRef) {
            popperRef.current = popper;
        }
    }, [popper]);

    return (

        <BootstrapPopover
            popper={popper}
            show={show}
            {...props}
            ref={ref}
            className={classNames(
                Styles.popover,
                Styles[`message-${size}`],
                variant ? Styles[`popover-${variant}`] : "",
                popoverClassName,
            )}
            // arrowProps={hasArrow ? undefined : { style: { display: "none" } }}
        >
            {title && (
                <BootstrapPopover.Header className={Styles["popover-title"]}>{title}</BootstrapPopover.Header>
            )}
            <BootstrapPopover.Body>
                {children}
            </BootstrapPopover.Body>
        </BootstrapPopover>
    );
});

const Popover = React.forwardRef(({
    buttonClassName, title, children, size, text, placement,
    popoverClassName, hasArrow, iconSize, variant, iconVariant,
}: Props, ref: React.ForwardedRef<PopoverController | undefined | null>) => {
    const [shouldShow, setShouldShow] = useState(false);
    const popperRef = React.useRef<PopperRef>();
    const theme = useTheme();

    if (typeof ref === "function") {
        ref({
            hide: () => setShouldShow(false),
            scheduleUpdate: () => popperRef.current?.scheduleUpdate?.(),
        });
    } else if (ref) {
        // eslint-disable-next-line no-param-reassign
        ref.current = {
            scheduleUpdate: () => popperRef.current?.scheduleUpdate?.(),
            hide: () => setShouldShow(false),
        };
    }

    let buttonBody: ReactNode;
    if (!text) {
        if (!theme.isV3) {
            buttonBody = <FiHelpCircle />;
        } else if (variant === "tooltip") {
            buttonBody = (
                <Icon
                    size={iconSize ? ICON_SIZE_MAP[iconSize] : undefined}
                    variant="default"
                    icon={<RiInformationLine />}
                />
            );
        } else {
            buttonBody = iconVariant ? (
                <Icon
                    size={iconSize ? ICON_SIZE_MAP[iconSize] : undefined}
                    variant={iconVariant}
                    icon={<RiInformationLine />}
                />
            ) : <RiInformationLine />;
        }
    }

    return (
        <OverlayTrigger
            trigger="click"
            placement={placement}
            overlay={(
                <UpdatingPopper
                    popperRef={popperRef}
                    title={title}
                    hasArrow={hasArrow}
                    popoverClassName={popoverClassName}
                    size={size}
                    variant={variant}
                >
                    {children}
                </UpdatingPopper>
            )}
            rootClose
            flip
            show={shouldShow}
            onToggle={nextValue => setShouldShow(nextValue)}
        >
            <Button
                variant="none"
                className={classNames(
                    `p-0 ${buttonClassName} align-baseline`,
                    iconSize ? Styles[`btn-icon-${iconSize}`] : "",
                )}
                prefixIcon={buttonBody}
            >
                {text}
            </Button>
        </OverlayTrigger>
    );
});
export default Popover;

Popover.defaultProps = {
    buttonClassName: "",
    popoverClassName: "",
    variant: "primary",
    size: "sm",
    text: undefined,
    placement: "right",
    title: undefined,
    hasArrow: true,
    iconSize: "md",
    iconVariant: undefined,
};
